import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar, Box, Toolbar, IconButton, Typography, Menu, Container,
  Avatar, Button, Tooltip, MenuItem, SwipeableDrawer, List, ListItemButton, ListItemText, Collapse, Divider
} from '@mui/material';
import { USER_ROLE_LABELS } from '../constants/userRoles';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { LanguageSwitcher } from './common/LanguageSwitcher';
import { securityConfig } from '../security/config';
import guardianLogo from '../assets/guardian-ai-logo.webp';
import guardianLogoWhite from '../assets/guardian-ai-logo-white.webp';
import { useTheme } from '@mui/material/styles';
import { PRODUCTS } from '../config/products';
import { useActiveProductSubscriptions } from '../hooks/useSubscription';

interface Page {
  title: string;
  path: string;
  requireAuth?: boolean;
  isFeature?: boolean;
}

const PaymentStatusIndicator = React.lazy(() =>
  import('./payment/PaymentStatusIndicator').then((mod) => ({
    default: mod.PaymentStatusIndicator,
  }))
);
const NotificationBell = React.lazy(() =>
  import('./notifications/NotificationBell').then((mod) => ({ default: mod.NotificationBell }))
);

function ResponsiveAppBar() {
  const { user, isAuthenticated, logout, isImpersonating, stopImpersonation } = useAuth();
  const { t } = useTranslation(['common', 'navigation']);
  const normalizedUserRole = String(user?.role ?? '').replace(/[-_\s]/g, '').toUpperCase();

  const pages: Page[] = React.useMemo(() => [
    { title: t('common:navigation.home', 'Home'), path: '/' },
    { title: 'Blog', path: '/blog', requireAuth: false },
  ], [t]);

  const productPages: Page[] = React.useMemo(() => [
    { title: 'Fellowvia', path: '/showroom/fellowvia', requireAuth: false },
    { title: 'CareConfirm', path: 'https://careconfirm.guardianai.network', requireAuth: false },
    { title: 'Site Recovery', path: 'https://siterecovery.guardianai.network/', requireAuth: false },
    { title: 'Commerce Recovery', path: 'https://recovery.guardianai.network/', requireAuth: false },
    { title: 'Family Care', path: '/family-care', requireAuth: false, productId: 'family-care' },
    { title: PRODUCTS.GOVERNABLE_AI.displayName, path: PRODUCTS.GOVERNABLE_AI.path, requireAuth: false, productId: 'governable-ai' },
    { title: 'DistroEngine', path: '/distro-engine', requireAuth: false, productId: 'distro-engine' },
    { title: 'CaptionEngine', path: '/caption-engine', requireAuth: false, productId: 'caption-engine' },
    { title: 'Media Proof', path: '/media-proof', requireAuth: false },
    { title: 'Rag Integrity Sentinel', path: '/rag-integrity-sentinel', requireAuth: false },
    { title: 'Native Tongue', path: '/native-tongue', requireAuth: false },
    { title: 'Referral Program', path: '/referral-program', requireAuth: false, productId: 'referral-program' },
  ] as (Page & { productId?: string })[], []);

  const servicesPages: (Page & { allowedProducts?: string[] })[] = React.useMemo(() => [
    { title: 'Services', path: '/services', requireAuth: false, allowedProducts: ['fraud-prevention', 'governable-ai', 'distro-engine', 'caption-engine', 'guardian-cli'] },
    { title: t('common:navigation.sandbox', 'Sandbox'), path: '/sandbox', requireAuth: false, allowedProducts: ['fraud-prevention', 'governable-ai', 'distro-engine', 'caption-engine', 'guardian-cli'] },
    { title: 'AI Agents', path: '/ai-agent', requireAuth: false, allowedProducts: ['fraud-prevention', 'governable-ai', 'distro-engine', 'caption-engine', 'guardian-cli'] },
  ], [t]);

  const resourcesPages: (Page & { allowedProducts?: string[] })[] = React.useMemo(() => [
    { title: 'The Book', path: '/go/governable-ai-book', requireAuth: false },
    { title: t('common:navigation.whitepaper', 'Whitepaper'), path: '/whitepaper', requireAuth: false, allowedProducts: ['fraud-prevention', 'governable-ai', 'distro-engine', 'caption-engine', 'guardian-cli'] },
    { title: 'Research Papers', path: '/research-papers', requireAuth: false, allowedProducts: ['fraud-prevention', 'governable-ai', 'distro-engine', 'caption-engine', 'guardian-cli'] },
    { title: 'Team', path: '/team', requireAuth: false },
  ], [t]);

  const enterprisePages: (Page & { productId?: string; allowedProducts?: string[] })[] = React.useMemo(() => [
    { title: t('common:navigation.vcn', 'Virtual Card Network (VCN)'), path: '/enterprise/vcn', requireAuth: false, allowedProducts: ['fraud-prevention', 'governable-ai', 'distro-engine', 'caption-engine', 'guardian-cli'] },
    { title: t('common:navigation.gateway_sniffer', 'GatewaySniffer'), path: '/enterprise/gateway-sniffer', requireAuth: false, allowedProducts: ['fraud-prevention', 'governable-ai', 'distro-engine', 'caption-engine', 'guardian-cli'] },
    { title: t('common:navigation.gatekeeperx', 'GateKeeperX'), path: '/enterprise/gatekeeperx', requireAuth: false, allowedProducts: ['fraud-prevention', 'governable-ai', 'distro-engine', 'caption-engine', 'guardian-cli'] },
    { title: t('common:navigation.guardian_infra', 'Guardian Infra'), path: '/cli-docs', requireAuth: false, productId: 'guardian-cli' },
  ], [t]);

  const userSettings: Page[] = React.useMemo(() => {
    let dashboardTitle = t('common:navigation.dashboard');
    let dashboardPath = '/dashboard';

    if (normalizedUserRole === 'SALESAGENT') {
      dashboardTitle = 'Sales Dashboard';
      dashboardPath = '/sales/dashboard';
    } else if (normalizedUserRole === 'CAREGIVER') {
      dashboardTitle = 'Caregiver Dashboard';
      dashboardPath = '/family-care/worker';
    }

    return [
      { title: dashboardTitle, path: dashboardPath, requireAuth: true },
      { title: t('common:navigation.profile'), path: '/dashboard/profile', requireAuth: true },
      { title: t('common:navigation.account_security', 'Account Security'), path: '/dashboard/2fa-setup', requireAuth: true },
      { title: t('common:navigation.sessions', 'Sessions'), path: '/dashboard/sessions', requireAuth: true },
      { title: t('common:navigation.social_accounts', 'Social Accounts'), path: '/dashboard/social-accounts', requireAuth: true },
      ...(normalizedUserRole === 'ADMIN' ? [{ title: 'Referral Admin', path: '/admin/sales', requireAuth: true }] : []),
      { title: 'Explore Other Products', path: '/', requireAuth: true },
    ];
  }, [normalizedUserRole, t]);

  // =========================================================================
  // SUBSCRIPTION-AWARE MENU FILTERING
  //
  // When authenticated, filter navigation menu categories/items to show only
  // products and pages relevant to the user's active subscriptions and role.
  // Admin sees everything. Non-authenticated users see the full catalog for discovery.
  // =========================================================================
  const { data: navSubscriptions } = useActiveProductSubscriptions();

  const navSubscribedProductIds = React.useMemo(() => {
    const ids = new Set<string>();
    if (navSubscriptions) {
      for (const sub of navSubscriptions) {
        const status = (sub as any)?.status?.toLowerCase?.() ?? '';
        if (status === 'active' || status === 'trialing') {
          const productId = (sub as any)?.productId ?? '';
          if (productId) ids.add(productId);
        }
      }
    }
    return ids;
  }, [navSubscriptions]);

  const isAdminRole = normalizedUserRole === 'ADMIN' || normalizedUserRole === 'SUPERADMIN';

  const filterNavPages = React.useCallback((pagesList: (Page & { productId?: string; allowedProducts?: string[] })[]) => {
    // Non-authenticated: show everything (product discovery / public pages)
    if (!isAuthenticated) return pagesList;
    // Admin: show everything
    if (isAdminRole) return pagesList;

    return pagesList.filter(page => {
      // 1. If page is tied to a specific product subscription
      if (page.productId) {
        if (normalizedUserRole === 'CAREGIVER' && page.productId === 'family-care') return true;
        if (normalizedUserRole === 'SALESAGENT' && page.productId === 'referral-program') return true;
        return navSubscribedProductIds.has(page.productId);
      }

      // 2. If page is only relevant for specific products
      if (page.allowedProducts) {
        if (normalizedUserRole === 'CAREGIVER' && page.allowedProducts.includes('family-care')) return true;
        if (normalizedUserRole === 'SALESAGENT' && page.allowedProducts.includes('referral-program')) return true;
        return page.allowedProducts.some(prodId => navSubscribedProductIds.has(prodId));
      }

      // Default: general page, visible to everyone
      return true;
    });
  }, [isAuthenticated, isAdminRole, normalizedUserRole, navSubscribedProductIds]);

  const visibleProductPages = React.useMemo(() => filterNavPages(productPages), [productPages, filterNavPages]);
  const visibleServicesPages = React.useMemo(() => filterNavPages(servicesPages), [servicesPages, filterNavPages]);
  const visibleResourcesPages = React.useMemo(() => filterNavPages(resourcesPages), [resourcesPages, filterNavPages]);
  const visibleEnterprisePages = React.useMemo(() => filterNavPages(enterprisePages), [enterprisePages, filterNavPages]);
  const theme = useTheme();
  const [prodOpen, setProdOpen] = React.useState(false);
  const [entOpen, setEntOpen] = React.useState(false);
  const [resOpen, setResOpen] = React.useState(false);
  const [servOpen, setServOpen] = React.useState(false);
  const navigate = useNavigate();
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [anchorElEnterprise, setAnchorElEnterprise] = React.useState<null | HTMLElement>(null);
  const [anchorElResources, setAnchorElResources] = React.useState<null | HTMLElement>(null);
  const [anchorElProducts, setAnchorElProducts] = React.useState<null | HTMLElement>(null);
  const [anchorElServices, setAnchorElServices] = React.useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleOpenEnterpriseMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElEnterprise(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenProductsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElProducts(event.currentTarget);
  };

  const handleCloseProductsMenu = () => {
    setAnchorElProducts(null);
  };

  const handleOpenServicesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElServices(event.currentTarget);
  };

  const handleCloseServicesMenu = () => {
    setAnchorElServices(null);
  };

  const handleOpenResourcesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElResources(event.currentTarget);
  };

  const handleCloseResourcesMenu = () => {
    setAnchorElResources(null);
  };

  const handleCloseEnterpriseMenu = () => {
    setAnchorElEnterprise(null);
  };

  const handleLogout = async () => {
    await logout();
    handleCloseUserMenu();
    handleCloseEnterpriseMenu();
    handleCloseResourcesMenu();
    handleCloseProductsMenu();
    handleCloseServicesMenu();
    navigate('/login');
  };

  const handleNavigation = (page: Page) => {
    handleCloseNavMenu();
    handleCloseUserMenu();
    handleCloseEnterpriseMenu();
    handleCloseResourcesMenu();
    handleCloseProductsMenu();
    handleCloseServicesMenu();
    if (page.path.startsWith('http') || page.path.startsWith('/go/')) {
      window.open(page.path, '_blank');
      return;
    }
    if (page.requireAuth && !isAuthenticated) {
      const isEnterprisePage = enterprisePages.some(ep => ep.path === page.path);
      if (isEnterprisePage && !securityConfig.enforceAuthForEnterprise) {
        navigate(page.path);
      } else {
        navigate('/login', { state: { from: page.path } });
      }
    } else {
      navigate(page.path);
    }
  };

  const visibleMobileNavPages = pages;
  const visibleDesktopNavPages = pages;

  const logoTypographyStyles = {
    mr: { xs: 0, sm: 1, md: 2 },
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 500,
    letterSpacing: { xs: '0.01rem', sm: '.05rem', md: '.1rem' },
    color: 'inherit',
    textDecoration: 'none',
    textTransform: 'uppercase',
    fontSize: { xs: '0.85rem', sm: '1.1rem', md: '1.5rem' },
    whiteSpace: 'nowrap',
    '& span': {
      fontWeight: 300,
      marginLeft: { xs: '0.1rem', sm: '0.25rem', md: '0.5rem' },
    }
  };

  // Determine which logo to use based on theme (dark or light background)
  const logoSrc = theme.palette.primary.main === '#0A2B4C' ? guardianLogoWhite : guardianLogo;

  return (
    <>
      {isImpersonating && (
        <Box sx={{ width: '100%', bgcolor: '#d32f2f', color: '#fff', px: 2, py: 0.5, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1400, position: 'fixed', top: 0, left: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            You are currently impersonating {user?.fullName}.
          </Typography>
          <Button 
            size="small" 
            variant="contained" 
            sx={{ bgcolor: '#fff', color: '#d32f2f', '&:hover': { bgcolor: '#f5f5f5' }, textTransform: 'none', py: 0, minHeight: 0, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} 
            onClick={() => {
              stopImpersonation();
              window.location.href = '/admin/sales';
            }}
          >
            Stop Impersonating
          </Button>
        </Box>
      )}
      <AppBar position="fixed" data-testid="appBar" sx={{ top: isImpersonating ? { xs: '36px', sm: '32px' } : 0 }}>
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
            minHeight: { xs: '56px', sm: '64px' },
            pr: { xs: 1 }
          }}
        >
          {/* Logo for AppBar (dark background) */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 2 }}>
            <img
              src={logoSrc}
              alt="Guardian AI Logo"
              width={40}
              height={40}
              decoding="async"
              fetchPriority="high"
              style={{ height: 40, width: 40, marginRight: 8 }}
            />
          </Box>

          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/"
            sx={{
              ...logoTypographyStyles,
              display: { xs: 'none', md: 'flex' },
            }}
          >
            Guardian<span>AI</span>
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="small"
              aria-label="navigation menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
              data-testid="mobile-menu-button"
              sx={{ mr: { xs: 0.5 }, minWidth: 44, minHeight: 44 }}
            >
              <MenuIcon sx={{ fontSize: { xs: '1.2rem' } }} />
            </IconButton>
            <SwipeableDrawer
              anchor="left"
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              onOpen={() => {}}
              PaperProps={{ sx: { width: 300, bgcolor: '#0b0e11', color: 'white', borderRight: '1px solid rgba(255,255,255,0.08)' } }}
            >
              {isAuthenticated && (
                <Box sx={{ px: 3, py: 3, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{user?.fullName}</Typography>
                  <Typography variant="caption" color="text.secondary">{USER_ROLE_LABELS[String(user?.role ?? '')]}</Typography>
                </Box>
              )}
              <List sx={{ py: 1 }}>
                {visibleMobileNavPages.map(page => (
                  <ListItemButton key={page.title} onClick={() => { handleNavigation(page); handleCloseNavMenu(); }}>
                    <ListItemText primary={page.title} />
                  </ListItemButton>
                ))}
              </List>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
              <List sx={{ py: 1 }}>
                {visibleProductPages.length > 0 && (
                  <>
                    <ListItemButton onClick={() => setProdOpen(prev => !prev)}>
                      <ListItemText primary="Products" />
                      {prodOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={prodOpen}>
                      <List component="div" disablePadding>
                        {visibleProductPages.map(page => (
                          <ListItemButton sx={{ pl: 4 }} key={page.title} onClick={() => { handleNavigation(page); handleCloseNavMenu(); }}>
                            <ListItemText primary={page.title} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </>
                )}

                {visibleServicesPages.length > 0 && (
                  <>
                    <ListItemButton onClick={() => setServOpen(prev => !prev)}>
                      <ListItemText primary="Services" />
                      {servOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={servOpen}>
                      <List component="div" disablePadding>
                        {visibleServicesPages.map(page => (
                          <ListItemButton sx={{ pl: 4 }} key={page.title} onClick={() => { handleNavigation(page); handleCloseNavMenu(); }}>
                            <ListItemText primary={page.title} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </>
                )}

                {visibleResourcesPages.length > 0 && (
                  <>
                    <ListItemButton onClick={() => setResOpen(prev => !prev)}>
                      <ListItemText primary="Resources" />
                      {resOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={resOpen}>
                      <List component="div" disablePadding>
                        {visibleResourcesPages.map(page => (
                          <ListItemButton sx={{ pl: 4 }} key={page.title} onClick={() => { handleNavigation(page); handleCloseNavMenu(); }}>
                            <ListItemText primary={page.title} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </>
                )}

                {visibleEnterprisePages.length > 0 && (
                  <>
                    <ListItemButton onClick={() => setEntOpen(prev => !prev)}>
                      <ListItemText primary={t('common:navigation.enterprise', 'Enterprise')} />
                      {entOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse in={entOpen}>
                      <List component="div" disablePadding>
                        {visibleEnterprisePages.map(page => (
                          <ListItemButton sx={{ pl: 4 }} key={page.title} onClick={() => { handleNavigation(page); handleCloseNavMenu(); }}>
                            <ListItemText primary={page.title} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  </>
                )}
              </List>
            </SwipeableDrawer>
          </Box>

          {/* Logo for AppBar (dark background) */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', mr: 1 }}>
            <img
              src={logoSrc}
              alt="Guardian AI Logo"
              width={32}
              height={32}
              decoding="async"
              fetchPriority="high"
              style={{ height: 32, width: 32, marginRight: 6 }}
            />
          </Box>

          <Typography
            variant="h5"
            noWrap
            component={Link}
            to="/"
            sx={{
              ...logoTypographyStyles,
              display: { xs: isAuthenticated ? 'none' : 'flex', md: 'none' },
              flexGrow: 1,
            }}
          >
            Guardian<span>AI</span>
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {visibleDesktopNavPages.map((page) => (
              <Button
                key={page.title}
                onClick={() => handleNavigation(page)}
                sx={{ my: 2, color: 'white', display: 'block' }}
                data-testid={`desktop-menu-item-${page.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {page.title}
              </Button>
            ))}
            {visibleProductPages.length > 0 && (
              <Button
                onClick={handleOpenProductsMenu}
                sx={{ my: 2, color: 'white', display: 'flex', alignItems: 'center' }}
                endIcon={<ExpandMoreIcon />}
                data-testid="desktop-menu-item-products"
                aria-controls="menu-products"
                aria-haspopup="true"
                aria-expanded={anchorElProducts ? 'true' : undefined}
              >
                Products
              </Button>
            )}
            {visibleServicesPages.length > 0 && (
              <Button
                onClick={handleOpenServicesMenu}
                sx={{ my: 2, color: 'white', display: 'flex', alignItems: 'center' }}
                endIcon={<ExpandMoreIcon />}
                data-testid="desktop-menu-item-services"
                aria-controls="menu-services"
                aria-haspopup="true"
                aria-expanded={anchorElServices ? 'true' : undefined}
              >
                Services
              </Button>
            )}
            {visibleResourcesPages.length > 0 && (
              <Button
                onClick={handleOpenResourcesMenu}
                sx={{ my: 2, color: 'white', display: 'flex', alignItems: 'center' }}
                endIcon={<ExpandMoreIcon />}
                data-testid="desktop-menu-item-resources"
                aria-controls="menu-resources"
                aria-haspopup="true"
                aria-expanded={anchorElResources ? 'true' : undefined}
              >
                Resources
              </Button>
            )}
            {visibleEnterprisePages.length > 0 && (
              <Button
                onClick={handleOpenEnterpriseMenu}
                sx={{ my: 2, color: 'white', display: 'flex', alignItems: 'center' }}
                endIcon={<ExpandMoreIcon />}
                data-testid="desktop-menu-item-enterprise"
                aria-controls="menu-enterprise"
                aria-haspopup="true"
                aria-expanded={anchorElEnterprise ? 'true' : undefined}
              >
                {t('common:navigation.enterprise', 'Enterprise')}
              </Button>
            )}
          </Box>

          <Menu
            id="menu-products"
            anchorEl={anchorElProducts}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorElProducts)}
            onClose={handleCloseProductsMenu}
            MenuListProps={{ 'aria-labelledby': 'desktop-menu-item-products' }}
          >
            {visibleProductPages.map((page) => (
              <MenuItem
                key={page.title}
                onClick={() => handleNavigation(page)}
                data-testid={`products-menu-item-${page.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Typography textAlign="left">{page.title}</Typography>
              </MenuItem>
            ))}
          </Menu>

          <Menu
            id="menu-services"
            anchorEl={anchorElServices}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorElServices)}
            onClose={handleCloseServicesMenu}
            MenuListProps={{ 'aria-labelledby': 'desktop-menu-item-services' }}
          >
            {visibleServicesPages.map((page) => (
              <MenuItem
                key={page.title}
                onClick={() => handleNavigation(page)}
                data-testid={`services-menu-item-${page.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Typography textAlign="left">{page.title}</Typography>
              </MenuItem>
            ))}
          </Menu>

          <Menu
            id="menu-enterprise"
            anchorEl={anchorElEnterprise}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorElEnterprise)}
            onClose={handleCloseEnterpriseMenu}
            MenuListProps={{ 'aria-labelledby': 'desktop-menu-item-enterprise' }}
          >
            {visibleEnterprisePages.map((page) => (
              <MenuItem
                key={page.title}
                onClick={() => handleNavigation(page)}
                data-testid={`enterprise-menu-item-${page.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Typography textAlign="left">{page.title}</Typography>
              </MenuItem>
            ))}
          </Menu>

          <Menu
            id="menu-resources"
            anchorEl={anchorElResources}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorElResources)}
            onClose={handleCloseResourcesMenu}
            MenuListProps={{ 'aria-labelledby': 'desktop-menu-item-resources' }}
          >
            {visibleResourcesPages.map((page) => (
              <MenuItem
                key={page.title}
                onClick={() => handleNavigation(page)}
                data-testid={`resources-menu-item-${page.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Typography textAlign="left">{page.title}</Typography>
              </MenuItem>
            ))}
          </Menu>

          <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
            {/* Language switcher - hide on mobile if authenticated to save space */}
            <Box sx={{ display: { xs: isAuthenticated ? 'none' : 'block', sm: 'block' } }}>
              <LanguageSwitcher />
            </Box>

            {isAuthenticated ? (
              <>
                <React.Suspense fallback={null}>
                  <PaymentStatusIndicator />
                </React.Suspense>
                <React.Suspense fallback={null}>
                  <NotificationBell />
                </React.Suspense>

                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: { xs: 0.5, sm: 1 }, ml: { xs: 0.25, sm: 1 }, minWidth: { sm: 44 }, minHeight: { sm: 44 } }}>
                    <Avatar
                      alt={user?.fullName || user?.email || 'User'}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        color: 'white',
                        width: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}
                    >
                      <PersonIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar-user"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {/* Show user info */}
                  <MenuItem disabled>
                    <Typography textAlign="center">
                      {user?.fullName || user?.email || 'User'}
                      {user?.role && (
                        <>
                          {' '}
                          (
                          {USER_ROLE_LABELS[user.role] || user.role}
                          )
                        </>
                      )}
                    </Typography>
                  </MenuItem>
                  {/* Show user settings for all users */}
                  {userSettings.map((setting) => (
                    <MenuItem
                      key={setting.title}
                      onClick={() => handleNavigation(setting)}
                      data-testid={`user-menu-item-${setting.title.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Typography textAlign="center">{setting.title}</Typography>
                    </MenuItem>
                  ))}
                  {isImpersonating && (
                    <MenuItem onClick={() => { stopImpersonation(); handleCloseUserMenu(); navigate('/admin/sales'); }}>
                      <Typography textAlign="center" color="warning.main" fontWeight="bold">Stop Impersonating</Typography>
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleLogout}>
                    <Typography textAlign="center">{t('common:navigation.logout')}</Typography>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button color="inherit" component={Link} to="/login" sx={{ ml: 1 }}>
                {t('common:login', 'Login')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
    </>
  );
}

export default ResponsiveAppBar;
